import React, { useRef } from 'react';
import { Box, Button, Flex, Heading, HStack, IconButton, Text } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { WhatsAppIcon } from './WhatsAppIcon';
import { Reveal } from './Reveal';
import { SectionHeading } from './SectionHeading';
import { services, type Service } from '../data/content';
import { whatsappHref } from '../data/brand';
function ServiceCard({
  service
}: {
  service: Service;
}) {
  return <Box as="article" role="group" position="relative" flex="0 0 auto" w={{
    base: '17.5rem',
    sm: '20rem',
    md: '23rem'
  }} h={{
    base: '27rem',
    md: '30rem'
  }} scrollSnapAlign="center" borderRadius="0" overflow="hidden" bg="ink.900" border="1px solid" borderColor="cream.200" sx={{
    clipPath: 'polygon(0 0, 100% 0, 100% 92%, 88% 100%, 0 100%)'
  }} transition="transform 0.35s ease, box-shadow 0.35s ease" _hover={{
    transform: 'translateY(-6px)',
    boxShadow: '0 26px 60px rgba(13,13,13,0.35)'
  }} data-id="element-145" data-mp="13xpm5o8d3efh">
      <Box as="img" src={service.image} alt={service.imageAlt} loading="lazy" position="absolute" top={0} left={0} w="100%" h="70%" objectFit="cover" transition="transform 0.6s ease" _groupHover={{
      transform: 'scale(1.06)'
    }} data-id="element-146" data-mp="36ad50uh1wyk3" />
      <Box position="absolute" top={0} left={0} right={0} h="70%" bg="linear-gradient(180deg, rgba(13,13,13,0.15) 0%, rgba(13,13,13,0.85) 100%)" aria-hidden="true" data-id="element-147" data-mp="3q0qc0hi17nyn" />

      <Box position="absolute" left={0} right={0} bottom={0} px={{
      base: 5,
      md: 6
    }} pt={5} pb={6} bg="ink.900" borderTop="2px solid" borderColor="crimson.500" transform={{
      base: 'translateY(0)',
      md: 'translateY(calc(100% - 9.5rem))'
    }} transition="transform 0.45s cubic-bezier(0.22,1,0.36,1)" _groupHover={{
      transform: 'translateY(0)'
    }} _focusWithin={{
      transform: 'translateY(0)'
    }} data-id="element-148" data-mp="3g17u06lomc98">
        <Heading as="h3" fontSize={{
        base: 'xl',
        md: '1.7rem'
      }} color="gold.300" mb={3} data-id="element-149" data-mp="1ljseqx6ea0y4">
          {service.title}
        </Heading>
        <Text fontSize="sm" color="cream.200" opacity={0.85} mb={5} data-id="element-150" data-mp="1gpo689hhdkux">
          {service.description}
        </Text>
        <Button as="a" href={whatsappHref(service.title)} target="_blank" rel="noopener noreferrer" variant="whatsapp" w="100%" h="2.9rem" fontSize="xs" leftIcon={<WhatsAppIcon size={16} data-id="element-152" data-mp="xty6ksx0t0k7" />} data-id="element-151" data-mp="16tl596yd7s9k">
          CONSULTAR AHORA
        </Button>
      </Box>
    </Box>;
}
export function Services() {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollByCard = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const amount = Math.min(track.clientWidth * 0.8, 380);
    track.scrollBy({
      left: amount * direction,
      behavior: 'smooth'
    });
  };
  return <Box as="section" id="servicios" bg="cream.50" color="ink.900" position="relative" pt={{
    base: 16,
    md: 28
  }} pb={{
    base: 16,
    md: 24
  }} mt={{
    md: '-4vw'
  }} sx={{
    clipPath: {
      base: 'none',
      md: 'polygon(0 4vw, 100% 0, 100% 100%, 0 100%)'
    }
  }} data-id="element-153" data-mp="2upj986wmi0sn">
      <Box px={{
      base: 5,
      md: 10,
      xl: 16
    }} data-id="element-154" data-mp="2bsrgz7z3sbcg">
        <Reveal data-id="element-155" data-mp="2bu2lja0dhn4m">
          <SectionHeading eyebrow="Acompañamiento espiritual" title="Nuestros Servicios" description="Cada consulta se realiza de manera personalizada. Conversamos contigo sobre tu situación y te explicamos en qué consiste cada práctica antes de comenzar." data-id="element-156" data-mp="2bucft9sgagg4" />
        </Reveal>

        <Flex justify="flex-end" mt={{
        base: 8,
        md: 10
      }} mb={4} data-id="element-157" data-mp="10glp3fmduga">
          <HStack spacing={2} data-id="element-158" data-mp="106td973rb7f">
            <IconButton aria-label="Ver servicios anteriores" icon={<ChevronLeftIcon size={20} data-id="element-160" data-mp="544s3isimjzy" />} onClick={() => scrollByCard(-1)} variant="crimsonOutline" borderRadius="full" size="lg" data-id="element-159" data-mp="521tiadarijm" />
            <IconButton aria-label="Ver más servicios" icon={<ChevronRightIcon size={20} data-id="element-162" data-mp="dknaj1hr4935" />} onClick={() => scrollByCard(1)} variant="crimsonOutline" borderRadius="full" size="lg" data-id="element-161" data-mp="3uta7hh39mpzn" />
          </HStack>
        </Flex>
      </Box>

      <Box ref={trackRef} className="hide-scrollbar" display="flex" gap={{
      base: 4,
      md: 6
    }} overflowX="auto" px={{
      base: 5,
      md: 10,
      xl: 16
    }} pb={4} sx={{
      scrollSnapType: 'x mandatory',
      WebkitOverflowScrolling: 'touch',
      overscrollBehaviorX: 'contain'
    }} data-id="element-163" data-mp="hkxfcatt4tgf">
        {services.map(service => <ServiceCard key={service.title} service={service} data-id="element-164" data-mp="118wojh8q3sjq" />)}
      </Box>

      <Text px={{
      base: 5,
      md: 10,
      xl: 16
    }} mt={4} fontSize="xs" color="ink.700" opacity={0.6} display={{
      base: 'block',
      md: 'none'
    }} data-id="element-165" data-mp="1jwxhjzoyzb3l">
        Desliza para ver todos los servicios →
      </Text>
    </Box>;
}